import {I18nKeys} from '@multipass-i18n';
import {Badge, buttonClassName, Container, IconChip, IconChipTone} from '@multipass-ui-components';
import {BarChart3, BookOpen, Check, CloudOff, Headphones, Layers, Lightbulb, List, Lock, LucideIcon, Mic, Pencil, Plane, Play, Plus, RefreshCw, Search, SquareStack} from 'lucide-react';
import * as React from 'react';

import {useT} from '../../logic';
import {PortalFooter} from '../shell/PortalFooter';
import {RouterLink} from '../shell/RouterLink';
import {CardShapeSwitcher} from './CardShapeSwitcher';
import {PhoneFrame} from './PhoneFrame';

const btnPrimaryLg = buttonClassName({size: 'lg'});

const Hero: React.FunctionComponent = () => {
    const t = useT();
    return (
        <section className="pb-12 pt-16 text-center">
            <Container>
                <Badge variant="brand" className="mb-5">
                    {t(I18nKeys.howItWorks.heroBadge)}
                </Badge>
                <h1 className="mx-auto mb-4 max-w-[16ch] text-display font-bold">
                    {t(I18nKeys.howItWorks.heroTitle)}
                </h1>
                <p className="mx-auto mb-6 max-w-[58ch] text-body-lg text-text-secondary">
                    {t(I18nKeys.howItWorks.heroBody)}
                </p>
                <div className="flex flex-wrap justify-center gap-3">
                    <RouterLink to="/signup" className={btnPrimaryLg}>{t(I18nKeys.common.createFreeAccount)}</RouterLink>
                </div>
            </Container>
        </section>
    );
};

const jumpLinkClass = 'inline-flex min-h-[38px] items-center gap-[7px] rounded-pill border border-border-subtle bg-surface px-[15px] text-label font-semibold text-text-secondary no-underline hover:border-border hover:text-brand-emphasis';

const JumpNav: React.FunctionComponent = () => {
    const t = useT();
    const anchors: {href: string; icon: LucideIcon; label: string}[] = [
        {href: '#blocks', icon: Layers, label: t(I18nKeys.howItWorks.jumpBlocks)},
        {href: '#plan', icon: List, label: t(I18nKeys.howItWorks.jumpPlan)},
        {href: '#cards', icon: SquareStack, label: t(I18nKeys.howItWorks.jumpCards)},
        {href: '#skills', icon: BarChart3, label: t(I18nKeys.howItWorks.jumpSkills)},
        {href: '#offline', icon: CloudOff, label: t(I18nKeys.howItWorks.jumpOffline)},
    ];
    return (
        <nav aria-label={t(I18nKeys.howItWorks.jumpLabel)} className="flex flex-wrap justify-center gap-2 px-5 pb-12">
            {anchors.map(({href, icon: Icon, label}) => (
                <a key={href} href={href} className={jumpLinkClass}>
                    <Icon size={15} className="text-text-muted"/> {label}
                </a>
            ))}
        </nav>
    );
};

interface ExplainerPoint {
    icon: LucideIcon;
    tone: IconChipTone;
    title: string;
    body: string;
}

interface ExplainerRowProps {
    id: string;
    number: number;
    eyebrow: string;
    title: string;
    lead: string;
    points: ExplainerPoint[];
    tint?: boolean;
    flip?: boolean;
    reservedControlSpace?: boolean;
    children: React.ReactNode;
}

const ExplainerRow: React.FunctionComponent<ExplainerRowProps> = ({id, number, eyebrow, title, lead, points, tint, flip, reservedControlSpace, children}: ExplainerRowProps) => (
    <section
        id={id}
        className={`border-t border-border-subtle pt-16 scroll-mt-[88px] ${reservedControlSpace ? 'pb-[calc(var(--space-16)+168px)]' : 'pb-16'} ${tint ? 'hiw-row--tint' : ''}`}
    >
        <Container className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-12 max-[900px]:grid-cols-1 max-[900px]:justify-items-center max-[900px]:gap-10">
            <div className={flip ? 'min-[901px]:order-2' : ''}>
                <span className="mb-4 inline-flex items-center gap-2 text-caption font-bold uppercase tracking-[.04em] text-brand-emphasis">
                    <span className="grid h-6 w-6 place-items-center rounded-full bg-brand/15 text-[12px] tracking-normal">{number}</span>
                    {eyebrow}
                </span>
                <h2 className="mb-4 max-w-[18ch] text-headline max-[900px]:max-w-[60ch]">{title}</h2>
                <p className="mb-6 max-w-[46ch] text-body-lg text-text-secondary max-[900px]:max-w-[60ch]">{lead}</p>
                <ul className="grid max-w-[46ch] list-none gap-4 max-[900px]:max-w-[60ch]">
                    {points.map(({icon, tone, title: pointTitle, body}) => (
                        <li key={pointTitle} className="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-3">
                            <IconChip icon={icon} tone={tone} size="sm"/>
                            <div>
                                <b className="mb-[2px] block text-label font-bold">{pointTitle}</b>
                                <p className="text-label text-text-secondary">{body}</p>
                            </div>
                        </li>
                    ))}
                </ul>
            </div>
            {children}
        </Container>
    </section>
);

const ClosingCta: React.FunctionComponent = () => {
    const t = useT();
    return (
        <section className="border-t border-border-subtle bg-surface py-16 text-center">
            <Container>
                <h2 className="mb-3 text-headline">{t(I18nKeys.howItWorks.closingTitle)}</h2>
                <p className="mx-auto mb-6 max-w-[48ch] text-text-secondary">{t(I18nKeys.howItWorks.closingBody)}</p>
                <RouterLink to="/signup" className={btnPrimaryLg}>{t(I18nKeys.common.createFreeAccount)}</RouterLink>
            </Container>
        </section>
    );
};

export const HowItWorksPage: React.FunctionComponent = () => {
    const t = useT();
    const keys = I18nKeys.howItWorks;
    return (
        <>
            <Hero/>
            <JumpNav/>
            <ExplainerRow
                id="blocks" number={1} tint
                eyebrow={t(keys.blocksEyebrow)} title={t(keys.blocksTitle)} lead={t(keys.blocksLead)}
                points={[
                    {icon: BookOpen, tone: 'brand', title: t(keys.blocksPoint1Title), body: t(keys.blocksPoint1Body)},
                    {icon: List, tone: 'accent', title: t(keys.blocksPoint2Title), body: t(keys.blocksPoint2Body)},
                    {icon: Plus, tone: 'brand', title: t(keys.blocksPoint3Title), body: t(keys.blocksPoint3Body)},
                ]}
            >
                <PhoneFrame daySrc="/app-screens/add-or-remove-blocks.day.png" nightSrc="/app-screens/add-or-remove-blocks.night.png" alt={t(keys.blocksShotAlt)}/>
            </ExplainerRow>
            <ExplainerRow
                id="plan" number={2} flip
                eyebrow={t(keys.planEyebrow)} title={t(keys.planTitle)} lead={t(keys.planLead)}
                points={[
                    {icon: Check, tone: 'success', title: t(keys.planPoint1Title), body: t(keys.planPoint1Body)},
                    {icon: Lock, tone: 'brand', title: t(keys.planPoint2Title), body: t(keys.planPoint2Body)},
                    {icon: Search, tone: 'accent', title: t(keys.planPoint3Title), body: t(keys.planPoint3Body)},
                ]}
            >
                <PhoneFrame daySrc="/app-screens/study-plan.day.png" nightSrc="/app-screens/study-plan.night.png" alt={t(keys.planShotAlt)}/>
            </ExplainerRow>
            <ExplainerRow
                id="cards" number={3} tint reservedControlSpace
                eyebrow={t(keys.cardsEyebrow)} title={t(keys.cardsTitle)} lead={t(keys.cardsLead)}
                points={[
                    {icon: Check, tone: 'brand', title: t(keys.cardsPoint1Title), body: t(keys.cardsPoint1Body)},
                    {icon: Mic, tone: 'accent', title: t(keys.cardsPoint2Title), body: t(keys.cardsPoint2Body)},
                    {icon: Play, tone: 'brand', title: t(keys.cardsPoint3Title), body: t(keys.cardsPoint3Body)},
                ]}
            >
                <CardShapeSwitcher/>
            </ExplainerRow>
            <ExplainerRow
                id="skills" number={4} flip
                eyebrow={t(keys.skillsEyebrow)} title={t(keys.skillsTitle)} lead={t(keys.skillsLead)}
                points={[
                    {icon: Pencil, tone: 'writing', title: t(keys.skillsPoint1Title), body: t(keys.skillsPoint1Body)},
                    {icon: Lightbulb, tone: 'meaning', title: t(keys.skillsPoint2Title), body: t(keys.skillsPoint2Body)},
                    {icon: Check, tone: 'brand', title: t(keys.skillsPoint3Title), body: t(keys.skillsPoint3Body)},
                ]}
            >
                <PhoneFrame daySrc="/app-screens/progress.day.png" nightSrc="/app-screens/progress.night.png" alt={t(keys.skillsShotAlt)}/>
            </ExplainerRow>
            <ExplainerRow
                id="offline" number={5} tint
                eyebrow={t(keys.offlineEyebrow)} title={t(keys.offlineTitle)} lead={t(keys.offlineLead)}
                points={[
                    {icon: Plane, tone: 'brand', title: t(keys.offlinePoint1Title), body: t(keys.offlinePoint1Body)},
                    {icon: Headphones, tone: 'accent', title: t(keys.offlinePoint2Title), body: t(keys.offlinePoint2Body)},
                    {icon: RefreshCw, tone: 'brand', title: t(keys.offlinePoint3Title), body: t(keys.offlinePoint3Body)},
                ]}
            >
                <PhoneFrame daySrc="/app-screens/learn-offline.day.png" nightSrc="/app-screens/learn-offline.night.png" alt={t(keys.offlineShotAlt)}/>
            </ExplainerRow>
            <ClosingCta/>
            <PortalFooter crossLink="home"/>
        </>
    );
};
